import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {KairosMark} from '@/components/kairos-mark'
import {AccionLigadaLink} from '@/components/accion-ligada-link'
import {CrearMejoraDialog} from '@/components/crear-mejora-dialog'
import {Campo} from '@/components/campo'
import {NativeSelect} from '@/components/native-select'
import {ReadOnlyNotice} from '@/components/read-only-notice'
import {SectionCard} from '@/components/section-card'
import {BloqueInterno} from '@/components/investigacion/bloque-interno'
import {PasoCausas} from '@/components/investigacion/paso-causas'
import {PasosNav, PASOS, type Paso} from '@/components/investigacion/pasos-nav'
import {PersonPicker, type PersonPickerValue} from '@/components/investigacion/person-picker'
import {useIncidente, type Incidente} from '@/hooks/sgsst/incidentes/use-incidentes'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useHighlightParam} from '@/hooks/common/use-highlight-param'
import {InvestigacionEvidencia} from '@/components/investigacion-evidencia'
import {
  informeUrl,
  useAddMedida,
  useAddSeat,
  useCompletarInvestigacion,
  useInvestigacion,
  useInvestigacionEvidencia,
  useMarcarManual,
  usePatchInvestigacion,
  usePrefillEquipo,
  type EquipoRol,
  type EquipoSeat,
  type Investigacion,
} from '@/hooks/sgsst/incidentes/use-investigacion'
import {downloadBlob} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {formatShortDate, localToday} from '@/lib/risk'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

function PasoEquipo({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const prefill = usePrefillEquipo(businessId, incidenteId)
  const addSeat = useAddSeat(businessId, incidenteId)
  const [rol, setRol] = useState<EquipoRol>('jefe_area')
  const [person, setPerson] = useState<PersonPickerValue>({})
  const [error, setError] = useState<string | null>(null)

  const rosterRoles = inv.roster
  return (
    <div className="space-y-4" data-testid="paso-equipo">
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            className="min-h-11 sm:min-h-0"
            data-testid="equipo-prefill"
            disabled={prefill.isPending}
            onClick={() => prefill.mutate(undefined, {onError: () => undefined})}>
            {t('investigacion.equipo.prefill')}
          </Button>
        </div>
      )}

      <ul className="space-y-2">
        {inv.equipo.map((seat) => (
          <SeatRow key={seat.id} seat={seat} />
        ))}
        {inv.equipo.length === 0 && <p className="text-sm text-muted-foreground">{t('investigacion.equipo.vacio')}</p>}
      </ul>

      {inv.faltantes.length > 0 && (
        <div className="tone-amber rounded-lg p-3 text-sm" data-testid="equipo-faltantes">
          {t('investigacion.equipo.faltan', {
            roles: inv.faltantes.map((r) => t(`investigacion.rol.${r}`)).join(', '),
          })}
          {inv.licencia_faltante && <p className="mt-1">{t('investigacion.equipo.licenciaFaltante')}</p>}
        </div>
      )}

      {!readOnly && (
        <BloqueInterno titulo={t('investigacion.equipo.agregar')}>
          <Campo label={t('investigacion.equipo.rolLabel')} htmlFor="seat-rol">
            <NativeSelect
              id="seat-rol"
              data-testid="seat-rol"
              value={rol}
              onChange={(e) => setRol(e.target.value as EquipoRol)}>
              {rosterRoles.concat(['otro' as EquipoRol]).map((r) => (
                <option key={r} value={r}>
                  {t(`investigacion.rol.${r}`)}
                </option>
              ))}
            </NativeSelect>
          </Campo>
          <Campo label={t('investigacion.equipo.personaLabel')}>
            <PersonPicker businessId={businessId} value={person} onChange={setPerson} testId="seat-person" />
          </Campo>
          <Button
            size="sm"
            className="min-h-11 sm:min-h-0"
            data-testid="seat-add"
            disabled={!person.employee_id || addSeat.isPending}
            onClick={() => {
              setError(null)
              addSeat.mutate(
                {rol, employee_id: person.employee_id!},
                {onSuccess: () => setPerson({}), onError: (err) => setError(translateApiError(err))}
              )
            }}>
            {t('investigacion.equipo.agregarSubmit')}
          </Button>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </BloqueInterno>
      )}
    </div>
  )
}

function SeatRow({seat}: {seat: EquipoSeat}) {
  const {t} = useTranslation()
  const asignado = seat.nombre !== null
  return (
    <li
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-lg border border-l-[3px] border-border p-3',
        asignado ? 'border-l-primary' : 'border-l-border'
      )}
      data-testid={`seat-${seat.rol}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{t(`investigacion.rol.${seat.rol}`)}</p>
        <p className="text-xs text-muted-foreground">
          {seat.nombre ?? t('investigacion.equipo.sinAsignar')}
          {seat.jefe_porque && <span> · {seat.jefe_porque}</span>}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        {seat.required && <Tag tone="neutral">{t('investigacion.equipo.obligatorio')}</Tag>}
        {seat.rol === 'profesional_licencia_so' &&
          asignado &&
          (seat.licencia_ok ? (
            <Tag tone="green">{t('investigacion.equipo.licenciaOk')}</Tag>
          ) : (
            <Tag tone="red">{t('investigacion.equipo.licenciaNoVerificable')}</Tag>
          ))}
      </div>
    </li>
  )
}

function PasoCronologia({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const patch = usePatchInvestigacion(businessId, incidenteId)
  const [cronologia, setCronologia] = useState(inv.cronologia ?? '')
  const [testigos, setTestigos] = useState(inv.testigos ?? '')
  const {data: evidencia} = useInvestigacionEvidencia(businessId, incidenteId)
  return (
    <div className="space-y-4" data-testid="paso-cronologia">
      <Campo label={t('investigacion.cronologia.label')} htmlFor="cronologia-input">
        <Textarea
          // CRONO y no TEXTO: una cronología hora por hora de un evento de varios días usa los 8000.
          maxLength={tope('CRONO')}
          id="cronologia-input"
          data-testid="cronologia-input"
          value={cronologia}
          onChange={(e) => setCronologia(e.target.value)}
          minRows={4}
          placeholder={t('investigacion.cronologia.placeholder')}
          disabled={readOnly}
        />
      </Campo>
      <Campo label={t('investigacion.cronologia.testigos')} htmlFor="testigos-input" opcional>
        <Textarea
          // TEXTO2000: el escalón que el backend publica para testigos, entre NOTA (500) y TEXTO (5000).
          maxLength={tope('TEXTO2000')}
          id="testigos-input"
          data-testid="testigos-input"
          value={testigos}
          onChange={(e) => setTestigos(e.target.value)}
          minRows={2}
          disabled={readOnly}
        />
      </Campo>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid="cronologia-guardar"
        disabled={readOnly || patch.isPending}
        onClick={() => patch.mutate({cronologia, testigos})}>
        {t('investigacion.cronologia.guardar')}
      </Button>

      <div data-testid="cronologia-evidencia">
        <BloqueInterno
          titulo={t('investigacion.cronologia.evidenciaTitulo')}
          accion={
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('investigacion.cronologia.evidenciaTitulo')})}
              testId="cronologia-evidencia-tip">
              {t('investigacion.cronologia.evidenciaTip')}
            </HelpIcon>
          }>
          <InvestigacionEvidencia
            businessId={businessId}
            incidenteId={incidenteId}
            investigacionId={inv.id}
            items={evidencia ?? []}
            editable={!readOnly}
          />
        </BloqueInterno>
      </div>
    </div>
  )
}

/** Las medidas caen al plan como acciones con `investigacion_id`; crear abre el mismo `ActionFormDialog` (D4). */
function PasoMedidas({
  inv,
  incidente,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  incidente: Incidente
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const {data: business} = useBusiness(businessId)
  const addMedida = useAddMedida(businessId, incidenteId)
  // `null` = cerrado. La descripción arranca vacía en los dos caminos y la sugerencia derivada de la causa
  // va como placeholder: prellenada se leía como texto que la persona escribió.
  const [creando, setCreando] = useState<{causa: string | null} | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4" data-testid="paso-medidas">
      <ul className="space-y-2">
        {inv.medidas.map((m) => (
          <li
            key={m.id}
            className="rounded-lg border border-border p-3"
            data-testid="medida-row"
            data-highlight-id={`medida-${m.id}`}>
            <AccionLigadaLink
              businessId={businessId}
              accion={{id: m.id, descripcion: m.description, estado: m.status}}
              testId="medida-accion"
            />
            <p className="mt-0.5 text-xs text-muted-foreground">
              {m.responsable_nombre ?? t('investigacion.medidas.sinResponsable')} ·{' '}
              <span className="font-mono tabular-nums">{formatShortDate(m.target_date)}</span>
            </p>
          </li>
        ))}
        {inv.medidas.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('investigacion.medidas.vacio')}</p>
        )}
      </ul>

      {!readOnly && (
        <>
          <Button
            size="sm"
            className="min-h-11 sm:min-h-0"
            data-testid="medida-add"
            onClick={() => {
              setError(null)
              setCreando({causa: null})
            }}>
            {t('investigacion.medidas.agregarSubmit')}
          </Button>

          {inv.causas.length > 0 && (
            <BloqueInterno
              titulo={t('investigacion.medidas.desdeCausasTitulo')}
              accion={
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('investigacion.medidas.desdeCausasTitulo')})}
                  testId="medidas-desde-causas-tip">
                  {t('investigacion.medidas.desdeCausasTip')}
                </HelpIcon>
              }>
              <ul className="space-y-2">
                {inv.causas.map((c) => (
                  <li
                    key={c.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                    data-testid="medida-causa-row">
                    <div className="min-w-0">
                      <p className="text-sm">{c.descripcion}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{c.tipo_label}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="min-h-11 sm:min-h-0"
                      data-testid="medida-causa-crear"
                      onClick={() => {
                        setError(null)
                        setCreando({causa: c.descripcion})
                      }}>
                      {t('investigacion.medidas.desdeCausaCrear')}
                    </Button>
                  </li>
                ))}
              </ul>
            </BloqueInterno>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </>
      )}

      {creando && (
        <CrearMejoraDialog
          businessId={businessId}
          open
          onOpenChange={(open) => !open && setCreando(null)}
          origen={{
            etiqueta: t('mejora.origen.investigacion'),
            titulo: t('investigacion.medidas.origenTitulo', {
              tipo: t(`incidentes.tipo.${incidente.tipo}`),
              fecha: formatShortDate(incidente.evento_fecha),
              afectado: incidente.afectado_nombre ?? t('investigacion.medidas.sinAfectado'),
            }),
            detalle: creando.causa ?? incidente.descripcion_hechos,
            testId: 'crear-medida-investigacion-dialog',
          }}
          descripcion=""
          descripcionPlaceholder={
            creando.causa ? t('investigacion.medidas.desdeCausaDescripcion', {causa: creando.causa}) : undefined
          }
          responsableId={business?.legal_representative_id ?? null}
          onCrear={async (editado) => {
            setError(null)
            await addMedida
              .mutateAsync({
                description: editado.descripcion,
                responsible_employee_id: editado.responsible_employee_id,
                target_date: editado.target_date,
                resources: editado.resources,
              })
              .catch((err: unknown) => {
                setError(translateApiError(err))
                throw err
              })
          }}
        />
      )}
    </div>
  )
}

/** Paso 5 — Informe 1401 auto-generado (DOCX) + completar la investigación. */
function PasoInforme({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const completar = useCompletarInvestigacion(businessId, incidenteId)
  const [error, setError] = useState<string | null>(null)
  const completa = inv.estado === 'completa'
  return (
    <div className="space-y-3" data-testid="paso-informe">
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 sm:min-h-0"
        data-testid="informe-descargar"
        onClick={() => downloadBlob(informeUrl(businessId, incidenteId), 'informe-1401.docx')}>
        {t('investigacion.informe.descargar')}
      </Button>
      {completa ? (
        <div className="tone-green rounded-lg p-3 text-sm" data-testid="investigacion-completa">
          {t('investigacion.informe.yaCompleta')}
          {inv.estandar_0312_sugerido && (
            <p className="mt-1">{t('investigacion.informe.sugerido0312', {code: inv.estandar_0312_sugerido})}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            size="sm"
            className="min-h-11 sm:min-h-0"
            data-testid="investigacion-completar"
            disabled={readOnly || !inv.puede_cerrar || completar.isPending}
            onClick={() => {
              setError(null)
              completar.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
            }}>
            {t('investigacion.informe.completar')}
          </Button>
          {!inv.puede_cerrar && (
            <div
              className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
              data-testid="investigacion-completar-bloqueo">
              <span>
                {inv.licencia_faltante
                  ? t('investigacion.informe.bloqueoLicencia')
                  : t('investigacion.informe.bloqueoEquipo', {
                      roles: inv.faltantes.map((r) => t(`investigacion.rol.${r}`)).join(', '),
                    })}
              </span>
              {inv.licencia_faltante && (
                <PorQue
                  label={t('investigacion.porqueLabel')}
                  testId="investigacion-bloqueo-porque"
                  contenidoTestId="investigacion-bloqueo-porque-contenido">
                  {t('investigacion.informe.bloqueoLicenciaPorque')}
                </PorQue>
              )}
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
      {inv.recordatorio_ipevr && (
        <p className="text-xs text-muted-foreground">{t('investigacion.informe.recordatorioIpevr')}</p>
      )}
    </div>
  )
}

/** Camino manual paralelo (D-3): «ya investigué por fuera» → mínimo estructurado. */
function CaminoManual({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const marcar = useMarcarManual(businessId, incidenteId)
  const [descripcion, setDescripcion] = useState('')
  const [fecha, setFecha] = useState(localToday()) // nosemgrep: browser-clock-business-date -- date input default value, legitimate data-entry (not a compliance decision)
  const [error, setError] = useState<string | null>(null)
  const fechaHecha = inv.investigacion_fecha ?? localToday() // nosemgrep: browser-clock-business-date -- fallback "hecha el" date reads the browser clock when investigacion_fecha is unset; tracked debt, not fixed in this wave (FE-02)
  if (inv.es_manual) {
    return (
      <div className="surface p-4 text-sm" data-testid="investigacion-manual-hecha">
        {t('investigacion.manual.hecha', {fecha: formatShortDate(fechaHecha)})}
        <p className="mt-1 text-muted-foreground">{inv.equipo_externo_descripcion}</p>
      </div>
    )
  }
  if (readOnly) return null
  return (
    <details className="surface px-5 py-4" data-testid="investigacion-manual">
      <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium text-primary sm:min-h-0">
        {t('investigacion.manual.abrir')}
      </summary>
      <div className="mt-3 space-y-3">
        <Hint>{t('investigacion.manual.intro')}</Hint>
        <Campo label={t('investigacion.manual.quienLabel')} htmlFor="manual-descripcion">
          <Textarea
            maxLength={tope('TEXTO')}
            id="manual-descripcion"
            data-testid="manual-descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            minRows={2}
            placeholder={t('investigacion.manual.descripcionPlaceholder')}
          />
        </Campo>
        <Campo label={t('investigacion.manual.fechaLabel')} htmlFor="manual-fecha">
          <Input
            id="manual-fecha"
            type="date"
            data-testid="manual-fecha"
            value={fecha}
            max={localToday()} // nosemgrep: browser-clock-business-date -- date input max, legitimate data-entry (not a compliance decision)
            onChange={(e) => setFecha(e.target.value)}
            className="h-11 font-mono tabular-nums sm:h-9"
          />
        </Campo>
        <Button
          size="sm"
          variant="outline"
          className="min-h-11 sm:min-h-0"
          data-testid="manual-submit"
          disabled={descripcion.trim() === '' || marcar.isPending}
          onClick={() => {
            setError(null)
            marcar.mutate(
              {equipo_externo_descripcion: descripcion.trim(), investigacion_fecha: fecha},
              {onError: (err) => setError(translateApiError(err))}
            )
          }}>
          {t('investigacion.manual.submit')}
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </details>
  )
}

function parsePaso(value: string | null): Paso | null {
  return PASOS.find((p) => p === value) ?? null
}

export function InvestigacionPage() {
  const {t} = useTranslation()
  const {businessId, incidenteId} = useParams()
  const {data: incidente} = useIncidente(businessId, incidenteId)
  const {data: inv, isLoading} = useInvestigacion(businessId, incidenteId)
  const [searchParams] = useSearchParams()
  // `?paso=` solo decide con qué paso abre el asistente; después manda la barra, que no vive en la URL.
  const [paso, setPaso] = useState<Paso>(() => parsePaso(searchParams.get('paso')) ?? 'equipo')
  const godReadOnly = useGodModeReadOnly()
  useHighlightParam('medida', paso === 'medidas' && !!inv)

  if (isLoading || !inv || !incidente) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  // Cerrado bloquea toda la investigación; reabrir vive en el detalle del incidente.
  const cerrado = incidente.estado === 'cerrado'
  const readOnly = godReadOnly || cerrado
  const pasoIdx = PASOS.indexOf(paso)

  return (
    <div className="space-y-5" data-testid="investigacion-page">
      <div>
        <Link
          to={`/businesses/${businessId}/incidentes/${incidenteId}`}
          className="inline-flex min-h-8 items-center text-[13px] font-medium text-muted-foreground hover:text-foreground">
          {t('investigacion.volver')}
        </Link>
        <div className="mt-2 flex items-center gap-2">
          <KairosMark className="text-primary" />
          <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            {t('investigacion.eyebrow')} · {t(`incidentes.tipo.${incidente.tipo}`)}
          </p>
        </div>
        <h1 className="mt-1 font-heading text-2xl">{incidente.descripcion_hechos}</h1>
        {inv.estado === 'completa' && (
          <Tag tone="green" className="mt-2">
            {t('investigacion.estadoCompleta')}
          </Tag>
        )}
      </div>

      {godReadOnly && (
        <ReadOnlyNotice testId="investigacion-readonly-banner">{t('investigacion.readOnlyBanner')}</ReadOnlyNotice>
      )}
      {cerrado && (
        <ReadOnlyNotice testId="investigacion-cerrado-banner">{t('investigacion.cerradoBanner')}</ReadOnlyNotice>
      )}

      <PasosNav inv={inv} paso={paso} onPaso={setPaso} />

      <SectionCard
        title={`${pasoIdx + 1}. ${t(`investigacion.paso.${paso}`)}`}
        actions={
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t(`investigacion.paso.${paso}`)})}
            testId="investigacion-paso-tip">
            {t(`investigacion.pasoTip.${paso}`)}
          </HelpIcon>
        }
        bodyClassName="p-5">
        {paso === 'equipo' && (
          <PasoEquipo inv={inv} businessId={businessId!} incidenteId={incidenteId!} readOnly={readOnly} />
        )}
        {paso === 'cronologia' && (
          <PasoCronologia inv={inv} businessId={businessId!} incidenteId={incidenteId!} readOnly={readOnly} />
        )}
        {paso === 'causas' && (
          <PasoCausas
            inv={inv}
            businessId={businessId!}
            incidenteId={incidenteId!}
            readOnly={readOnly}
            problema={incidente.descripcion_hechos}
          />
        )}
        {paso === 'medidas' && (
          <PasoMedidas
            inv={inv}
            incidente={incidente}
            businessId={businessId!}
            incidenteId={incidenteId!}
            readOnly={readOnly}
          />
        )}
        {paso === 'informe' && (
          <PasoInforme inv={inv} businessId={businessId!} incidenteId={incidenteId!} readOnly={readOnly} />
        )}
      </SectionCard>

      <CaminoManual inv={inv} businessId={businessId!} incidenteId={incidenteId!} readOnly={readOnly} />
    </div>
  )
}
